import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import EditIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Edit.js';
import FilterIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Filter.js';
import UndoIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Undo.js';
import {
  PageHeader,
  StatusIndicator,
} from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
} from '@hello-pangea/dnd';
import React, { useState, useEffect } from 'react';
import { EmptyState } from 'src/components/atlas/molecules';
import {
  useGetOrganisationCustomFieldsQuery,
  useGetOrganisationCustomFieldSectionsQuery,
  GetOrganisationCustomFieldsQuery,
} from 'src/graphql/generated';
import { useCustomFieldMutations } from './hooks/useCustomFieldMutations';
import { CreateCustomFieldSideSheet } from './CreateCustomFieldSideSheet';
import { SectionsTab } from './SectionsTab';
import { CustomFieldsFilterPopover } from './CustomFieldsFilterPopover';
import { customFieldStatusColorMap } from './customFieldStatusColorMap';
import * as styles from './CustomFields.styles';
import { CustomFieldFilters } from './customFields.types';

export type CustomField =
  GetOrganisationCustomFieldsQuery['getOrganisationCustomFields']['customFields'][number];

const COLUMNS = [
  'Status',
  'Label',
  'Identifier',
  'Section',
  'Type',
  'Last updated',
  'Actions',
  'Created by',
];

// Default to no status filter so all fields — including retired (INACTIVE) —
// are listed; the table must show retired fields, not just active ones.
const EMPTY_FILTERS: CustomFieldFilters = { status: '', type: '' };
const PAGE_SIZE = 25;

const FIELD_TYPE_LABELS: Record<string, string> = {
  TEXT: 'Text input',
  SINGLE_SELECT: 'Single select',
};

// INACTIVE fields are surfaced as "Retired" in the UI (DB enum is ACTIVE/INACTIVE).
const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Retired',
};

export const formatCreatedBy = (createdBy: string): string => createdBy || '—';

export const CustomFields = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<CustomFieldFilters>(EMPTY_FILTERS);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [sectionsCreateSheetOpen, setSectionsCreateSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState<CustomField | undefined>(
    undefined,
  );

  const { reorderCustomFields } = useCustomFieldMutations();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const offset = (page - 1) * rowsPerPage;

  const { data, isLoading } = useGetOrganisationCustomFieldsQuery(
    {
      input: {
        limit: rowsPerPage,
        offset,
        search: debouncedSearch || undefined,
        status: filters.status || undefined,
        fieldType: filters.type || undefined,
      },
    },
    { enabled: true },
  );

  const customFields = data?.getOrganisationCustomFields?.customFields || [];

  const totalCount = data?.getOrganisationCustomFields?.total ?? 0;

  const { data: sectionsData } = useGetOrganisationCustomFieldSectionsQuery();
  const sectionNameById = new Map(
    (sectionsData?.getOrganisationCustomFieldSections?.sections ?? []).map(
      (section) => [section.id, section.name],
    ),
  );

  const handleReset = () => {
    setSearchTerm('');
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const formatDate = (date: Date | string) => {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const getFieldTypeLabel = (fieldType: string) => {
    return FIELD_TYPE_LABELS[fieldType] || fieldType;
  };

  const getStatusLabel = (status: string) => {
    return STATUS_LABELS[status] || status;
  };

  const handleEdit = (field: CustomField) => {
    setFieldToEdit(field);
    setEditSheetOpen(true);
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const reorderedFields = Array.from(customFields);
    const [movedField] = reorderedFields.splice(source.index, 1);
    reorderedFields.splice(destination.index, 0, movedField);

    const updatedFields = reorderedFields.map((field, index) => ({
      ...field,
      displayOrder: index,
    }));

    reorderCustomFields({
      draggedFieldId: movedField.id,
      values: updatedFields.map((field) => ({
        id: field.id,
        displayOrder: field.displayOrder,
      })),
    });
  };

  return (
    <Box sx={styles.pageContainer} data-testid="custom-fields-page">
      <PageHeader
        pageTitle="Field management"
        pageSubtitle="Manage custom field settings"
        moreButton={
          activeTab === 0 ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              data-testid="custom-fields-add-new"
              onClick={() => setCreateSheetOpen(true)}
            >
              Add new field
            </Button>
          ) : undefined
        }
      />

      <Tabs
        aria-label="Field management tabs"
        value={activeTab}
        onChange={(_, next: number) => setActiveTab(next)}
        sx={styles.tabs}
      >
        <Tab label="Fields" data-testid="custom-fields-tab-fields" />
        <Tab label="Sections" data-testid="custom-fields-tab-sections" />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Box sx={styles.toolbar}>
            <Box sx={styles.searchField}>
              <Typography sx={styles.searchLabel}>Search</Typography>
              <TextField
                size="medium"
                value={searchTerm}
                placeholder="Search by all fields"
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  htmlInput: { 'data-testid': 'custom-fields-search' },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <span className="uil uil-search" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Button
              variant="text"
              startIcon={<FilterIcon />}
              data-testid="custom-fields-filter"
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                setFilterAnchorEl(e.currentTarget)
              }
            >
              Filter
            </Button>
            <CustomFieldsFilterPopover
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={() => setFilterAnchorEl(null)}
              value={filters}
              onChange={setFilters}
            />

            <Button
              variant="text"
              startIcon={<UndoIcon />}
              data-testid="custom-fields-reset"
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>

          <DragDropContext onDragEnd={onDragEnd}>
            <TableContainer sx={styles.tableContainer}>
              <Table sx={styles.table}>
                <TableHead>
                  <TableRow sx={styles.headerRow}>
                    {COLUMNS.map((column) => (
                      <TableCell key={column} sx={styles.headerCell}>
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <Droppable droppableId="custom-fields-table">
                  {(provided: DroppableProvided) => (
                    <TableBody
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {!isLoading &&
                        customFields.map((field, index) => (
                          <Draggable
                            key={field.id}
                            draggableId={field.id}
                            index={index}
                            isDragDisabled={field.isStandard}
                          >
                            {(provided: DraggableProvided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                data-testid={`custom-fields-row-${field.id}`}
                                sx={{
                                  cursor: field.isStandard ? 'default' : 'grab',
                                  ...(snapshot.isDragging && {
                                    display: 'table',
                                    tableLayout: 'fixed',
                                    width: '100%',
                                    backgroundColor: 'background.paper',
                                    boxShadow: 2,
                                  }),
                                }}
                              >
                                <TableCell sx={styles.statusCell}>
                                  <StatusIndicator
                                    label={getStatusLabel(field.status)}
                                    customColor={customFieldStatusColorMap(
                                      field.status,
                                    )}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell sx={styles.labelCell}>
                                  {field.label}
                                </TableCell>
                                <TableCell sx={styles.identifierCell}>
                                  {field.identifier}
                                </TableCell>
                                <TableCell sx={styles.sectionCell}>
                                  {(field.sectionId &&
                                    sectionNameById.get(field.sectionId)) ||
                                    'Ungrouped'}
                                </TableCell>
                                <TableCell sx={styles.typeCell}>
                                  <Chip
                                    variant="outlined"
                                    label={getFieldTypeLabel(field.fieldType)}
                                    sx={styles.typeChip}
                                  />
                                </TableCell>
                                <TableCell sx={styles.dateCell}>
                                  {formatDate(field.updatedAt)}
                                </TableCell>
                                <TableCell sx={styles.actionsCell}>
                                  <IconButton
                                    aria-label="Edit field"
                                    data-testid={`custom-fields-edit-${field.id}`}
                                    onClick={() => handleEdit(field)}
                                    disabled={field.isStandard}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={styles.cell}>
                                  {formatCreatedBy(field?.author?.name ?? '')}
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
              {!isLoading && customFields.length === 0 && (
                <Box
                  sx={styles.emptyWrapper}
                  data-testid="custom-fields-empty-state"
                >
                  <EmptyState
                    title="No custom fields found"
                    description={
                      debouncedSearch || filters.status || filters.type
                        ? 'Try adjusting your search or filters.'
                        : 'Custom fields for your organisation will appear here.'
                    }
                  />
                </Box>
              )}
              {!isLoading && customFields.length > 0 && (
                <Box sx={styles.paginationContainer}>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page - 1}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onPageChange={(_event, newPage) =>
                      handlePageChange(newPage + 1)
                    }
                    onRowsPerPageChange={(event) =>
                      handleRowsPerPageChange(parseInt(event.target.value, 10))
                    }
                    labelRowsPerPage="Rows per page"
                    data-testid="custom-fields-pagination"
                  />
                </Box>
              )}
            </TableContainer>
          </DragDropContext>
        </>
      )}

      {activeTab === 1 && (
        <SectionsTab
          createSheetOpen={sectionsCreateSheetOpen}
          onCloseCreateSheet={() => setSectionsCreateSheetOpen(false)}
        />
      )}

      <CreateCustomFieldSideSheet
        isOpen={createSheetOpen}
        onClose={() => setCreateSheetOpen(false)}
        onSuccess={() => setCreateSheetOpen(false)}
        mode="create"
      />

      <CreateCustomFieldSideSheet
        isOpen={editSheetOpen}
        onClose={() => {
          setEditSheetOpen(false);
          setFieldToEdit(undefined);
        }}
        onSuccess={() => {
          setEditSheetOpen(false);
          setFieldToEdit(undefined);
        }}
        mode="edit"
        fieldToEdit={fieldToEdit}
      />
    </Box>
  );
};
